import type { TFunction } from 'i18next';
import type { LiveVehicle, VehicleState } from './types';
import type { VehicleListItem } from '../vehicles/useVehicles';
import {
  buildStatusLine,
  stateHasLabel,
  STATE_TONE,
  type StateTone,
  type StatusFacts,
} from './vehicleState';

/**
 * The one-line status renderer (slice 13e) — the single place the three
 * vocabularies (vehicle state · tracker state · position age) are turned into
 * pixels, so /vehicles, the map side-list, the map sheet and the vehicle-detail
 * Live tab can never word the same facts differently. It renders exactly what
 * `buildStatusLine` returns and adds no copy of its own.
 *
 * Colour is tokens only (`text-raad-*`) — no hex literal — so the queued theme
 * slice is a palette change. `engineOn` carries the warn token to STAND OUT
 * (Mahad: the case that could mean "moving where we cannot see it"); it is a
 * label, not an alert, so there is no notification affordance here.
 */

// Each semantic tone → its business-side token class. Green is business, never the
// admin indigo. `engineOn` = warn (amber) is the prominent one; parked/idling are
// neutral slate; measurements and the tracker/"Not reporting" word are muted/dim.
const TONE_CLASS: Record<StateTone, string> = {
  moving: 'text-raad-accent',
  idling: 'text-raad-slate',
  // 56g — the PARKED word takes its own token, grey in light mode (Mahad, 22 Sep).
  // Idling stays `--raad-slate`. This class also backs `compactStateWord`'s parked
  // path (it reads TONE_CLASS[STATE_TONE.parked]) and the pill text below.
  parked: 'text-raad-parked',
  engineOn: 'text-raad-warn',
  trackerOn: 'text-raad-accent',
  trackerOff: 'text-raad-muted',
  measure: 'text-raad-muted',
};

/** Adapt the live model to the vocabulary's facts (speed included — the live
 *  surfaces hold it, so the label-less no-ignition case reads speed-and-age). */
export function liveFacts(v: LiveVehicle): StatusFacts {
  return {
    state: v.state,
    status: v.status,
    positionAgeSec: v.positionAgeSec,
    sats: v.trackerHealth?.sats ?? null,
    speedKmh: v.speedKmh,
    lastFixMoving: v.lastFixMoving,
  };
}

/** Adapt the list item to the vocabulary's facts. The list carries no speed, so it
 *  is omitted — the label-less states read tracker-and-age there, still honest. */
export function listFacts(v: VehicleListItem): StatusFacts {
  return {
    state: v.state,
    status: v.status,
    positionAgeSec: v.positionAgeSec,
    sats: v.trackerHealth?.sats ?? null,
    // 15e — the list has no speed, which is exactly why the discriminator is
    // server-derived: without it /vehicles could not choose the sub-line at all.
    lastFixMoving: v.lastFixMoving,
  };
}

/**
 * The full one-line status: segments joined by " · ", then the "No satellite view"
 * note appended with " — ". Used wherever the whole line fits (/vehicles rows, the
 * sheet's freshness block, the detail Live tab).
 */
export function VehicleStateLine({
  facts,
  t,
  className,
  omitState = false,
  omitTracker = false,
  omitSat = false,
}: {
  facts: StatusFacts;
  t: TFunction;
  className?: string;
  /** Drop the leading vehicle-state word — for surfaces that already show it as a
   *  pill (the map sheet, the detail Live tab), so it is never printed twice. */
  omitState?: boolean;
  /** Drop the tracker word — for surfaces that show the tracker-health block. */
  omitTracker?: boolean;
  /** Drop the "No satellite view" note — for surfaces that show the sat count. */
  omitSat?: boolean;
}) {
  const { segments, satNote } = buildStatusLine(t, facts, { omitState, omitTracker, omitSat });
  return (
    <span className={`inline-flex flex-wrap items-baseline gap-x-1.5 gap-y-0.5 leading-snug ${className ?? 'text-xs'}`}>
      {segments.map((seg, i) => (
        <span key={seg.key} className="inline-flex items-baseline">
          {i > 0 ? <span className="mr-1.5 text-raad-dim" aria-hidden="true">·</span> : null}
          <span className={`${TONE_CLASS[seg.tone]} ${seg.key === 'state' ? 'font-semibold' : ''}`}>
            {seg.text}
          </span>
        </span>
      ))}
      {satNote ? (
        <span className="inline-flex items-baseline">
          <span className="mr-1.5 text-raad-dim" aria-hidden="true">—</span>
          <span className="text-raad-muted">{satNote}</span>
        </span>
      ) : null}
    </span>
  );
}

/**
 * The compact row's single primary word, in the ONE vehicle-state vocabulary — for
 * the map side-list and the dashboard fleet preview, which show one word plus a
 * position age in the corner. The four states that make a claim print their word
 * (engineOn stands out in warn); the label-less states print the tracker fact
 * (Reporting / Not reporting), never "Unknown". Returns null when there is nothing
 * honest to say (unclear tracker, located) so the row simply omits the word.
 */
export function compactStateWord(
  t: TFunction,
  v: LiveVehicle,
  located: boolean,
): { text: string; className: string } | null {
  if (!located) return { text: t('livemap.list.noLocation'), className: 'text-raad-muted' };
  if (stateHasLabel(v.state)) {
    return { text: t(`vehicleState.${v.state}`), className: `${TONE_CLASS[STATE_TONE[v.state]]} font-semibold` };
  }
  if (v.status === 'online') return { text: t('trackerState.online'), className: TONE_CLASS.trackerOn };
  if (v.status === 'offline') return { text: t('trackerState.offline'), className: TONE_CLASS.trackerOff };
  return null;
}

/**
 * The compact vehicle-STATE pill for the map sheet and the detail header — the
 * word only, for the four states that make a claim; `noContact`/`unknown` render
 * NOTHING (the measurements line carries them), so "Unknown" never appears as a
 * pill. `engineOn` stands out in the warn family.
 */
export function VehicleStatePill({ state, t }: { state: VehicleState; t: TFunction }) {
  if (!stateHasLabel(state)) return null;
  const tone = STATE_TONE[state];
  // Each labelled state gets a faint tint of its own tone + the tone's text class.
  //
  // 🔴 `Record<VehicleState, string>` — EXHAUSTIVE, AND THE CAST THAT USED TO BE
  // HERE IS GONE. This map was keyed to the four labelled states with the lookup
  // cast to match (`tint[state as 'moving' | …]`), which compiled and then silently
  // produced `undefined` for any state outside those four. Slice 15e walked
  // straight into it: `engineStale` IS a labelled state, so the pill rendered with
  // a literal `undefined` in its class list. The cast was the whole defect — it
  // told the compiler to stop checking exactly where checking was the point.
  //
  // Every other state map in this feature is `Record<VehicleState, …>` for that
  // reason (STATE_HAS_LABEL, STATE_TONE, STATE_RANK, CHIP_DOT, CHIP_TEXT), and a
  // new member is a compile error in all of them. This one now joins them. The
  // label-less states are unreachable here (the guard above returns first) but are
  // still listed, because an entry that is never read costs nothing and a missing
  // key costs a silent `undefined`.
  const tint: Record<VehicleState, string> = {
    moving: 'bg-raad-accent/10',
    idling: 'bg-raad-slate/10',
    // 56g — parked pill tint follows the parked word token (grey in light mode);
    // idling above keeps `--raad-slate`.
    parked: 'bg-raad-parked/10',
    engineOn: 'bg-raad-warn/10',
    engineStale: 'bg-raad-muted/10',
    noContact: '',
    unknown: '',
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${tint[state]} ${TONE_CLASS[tone]}`}
    >
      {t(`vehicleState.${state}`)}
    </span>
  );
}
